import ProductDetailsFields, { ItemDetailsTabs, revealItemInvalid } from "./ProductDetailsFields";
import { isActivePurchaseSupplier } from "../../utils/purchaseSuppliers";
import useCatalogAccess from "../useCatalogAccess";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import { catalogRequestError, duplicateInternalCodeMessage, hasInternalCodeConflict } from "../internalCatalogCodes";
import ItemClassificationPicker from "./ItemClassificationPicker";
import ItemConsumptionFrequencies, { consumptionFrequencyDraft, consumptionUnits, serializeConsumptionFrequencies } from "./ItemConsumptionFrequencies";
import ItemUnitPicker from "./ItemUnitPicker";
import ItemFormNotice from "./ItemFormNotice";
import ItemUnitCompatibility, { useItemUnitCompatibility } from "./ItemUnitCompatibility";
import ItemSubunitPreview from "./ItemSubunitPreview";
import ItemParUnitSelect from "./ItemParUnitSelect";
import { parStockBaseQuantity, parStockUnits, serializeParStock } from "./itemParStockUnits";
import { getUnitName } from "../../utils/unitNames";
import { getLocationName } from "../../utils/locationNames";
import { purchaseConstraintFields, serializePurchaseConstraints } from "./itemPurchaseConstraints";
import { ITEM_UNIT_CONFIGURATION_FIELDS, itemUnitConfigurationChanged, itemUnitConfigurationError, itemUnitConfigurationsOverlap, itemUnitSaveError, originalItemUnitConfiguration } from "./itemUnitConfiguration";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiTrash2 } from "react-icons/fi";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import { itemCatalogText } from "../itemCatalogMessages";
import ItemAvailabilityControl, { isItemAvailabilityLocked } from "../ItemAvailabilityControl";
import useItemDialogFocus from "../useItemDialogFocus";
import { managedByAdmin } from "../../utils/tenderManagement";
import "../../PurchasingUi.css";
import "../ItemCatalog.css";
import "./ItemPurchaseFields.css";
import axios from "../../../api/axiosInstance";
import {
  inventoryPolicySelectOptionsForItem,
  isDirectConsumptionItem,
  readItemsFamiliesUser,
} from "../itemsFamiliesUi";

const buildEmptyForm = () => ({
  code: "",
  supplier_code: "",
  name: "",
  description: "",
  product_details: null,
  category_id: "",
  subcategory_id: "",
  inventory_policy: "",
  unit_id: "",
  subunit_id: "",
  pack_size: "",
  minimum_purchase_quantity: "",
  purchase_multiple: "",
  subunit2_id: "",
  subunit2_size: "",
  suppliers: [],
});

const cleanNullable = (value) => {
  if (value === "" || value === undefined) return null;
  return value;
};

const ITEM_SAVE_ERRORS = {
  ITEM_SUBCATEGORY_MISMATCH: "The selected subcategory does not belong to the item category. Select a matching subcategory or clear it before saving. No changes were saved.",
  ITEM_SUPPLIERS_INVALID: "The supplier selection is invalid. Review the linked suppliers before saving. No changes were saved.",
  ITEM_SUPPLIERS_STALE: "The item's suppliers have changed since you opened it. No changes were saved. Close and reopen the editor to review the current suppliers before saving.",
  ITEM_SUPPLIERS_UNAVAILABLE: "The supplier links could not be saved. No changes were saved. Reload the item and try again.",
  SUPPLIER_NOT_FOUND: "A selected supplier is no longer available in this business unit. No changes were saved. Reload the item and review its suppliers.",
  SUPPLIER_INACTIVE: "New supplier links require an active stock supplier. No changes were saved. Review the suppliers you added.",
  STOCK_SUPPLIER_REQUIRED: "New supplier links require an active stock supplier. No changes were saved. Review the suppliers you added.",
};

const EMPTY_PAR_STOCK = {
  general_par_quantity: "",
  general_unit_id: "",
  locations: [],
};

function toParInputValue(value) {
  return value === null || value === undefined ? "" : String(value);
}

export default function EditItemPopup({ isOpen, onClose, item, onUpdated, onAvailabilityUpdated, existingRecords = [], onOpenMerge, mergeOpen = false }) {
  const { themeId, languageId } = usePmsTheme();
  const t = useCallback((text) => itemCatalogText(languageId, text), [languageId]);
  const numberFormatter = useMemo(() => new Intl.NumberFormat(languageId === "es" ? "es-ES" : "en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 8 }), [languageId]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [form, setForm] = useState(buildEmptyForm());
  const [detailsTab, setDetailsTab] = useState("general");
  const [detailsBusy, setDetailsBusy] = useState(false);
  const detailsSession = useRef({ committed: false });
  const [showSubunit, setShowSubunit] = useState(false);
  const [showSubunit2, setShowSubunit2] = useState(false);
  const [categories, setCategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [subfamilies, setSubfamilies] = useState([]);
  const [consumptionFrequencies, setConsumptionFrequencies] = useState({});
  const [consumptionLocations, setConsumptionLocations] = useState([]);
  const [frequenciesAvailable, setFrequenciesAvailable] = useState(false);
  const [retryingFrequencies, setRetryingFrequencies] = useState(false);
  const frequencyScope = useRef(0);
  const loadedUnitConfig = useRef({});
  const loadedSupplierIds = useRef([]);
  const loadedQuantitySettings = useRef({ pars: null, frequencies: null });
  const [subcategories, setSubcategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availabilityBusy, setAvailabilityBusy] = useState(false);
  const [availabilityItem, setAvailabilityItem] = useState(null);
  const [error, setErrorMessage] = useState("");
  const [formNotice, setFormNotice] = useState(null);
  const formNoticeId = useRef(0);
  const formNoticeFocus = useRef(null);
  const setError = useCallback((message) => {
    setErrorMessage(message);
    setFormNotice(null);
  }, []);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [parStock, setParStock] = useState(EMPTY_PAR_STOCK);
  const [parStockAvailable, setParStockAvailable] = useState(false);

  const editorBusy = saving || availabilityBusy;
  const modalRef = useItemDialogFocus(isOpen && !pendingRequest, editorBusy || mergeOpen, () => {
    if (formNotice) dismissFormNotice();
    else onClose();
  });
  const dismissFormNotice = useCallback(() => {
    if (modalRef.current?.querySelector(".aip-notice")?.contains(document.activeElement)) {
      const target = formNoticeFocus.current?.isConnected ? formNoticeFocus.current : modalRef.current;
      target?.focus({ preventScroll: true });
    }
    setFormNotice(null);
  }, [modalRef]);
  const showRequestError = useCallback((failure, message) => {
    if (failure?.response?.status !== 403) { setError(message); return; }
    setErrorMessage("");
    formNoticeFocus.current = document.activeElement;
    // Each denied attempt gets its own full 1.5 seconds, even if the text repeats.
    setFormNotice({ id: ++formNoticeId.current,
      message: catalogRequestError(failure, "Access denied for this item.") });
  }, [setError]);
  const showNoChangesNotice = useCallback(() => {
    setErrorMessage("");
    formNoticeFocus.current = document.activeElement;
    setFormNotice({ id: ++formNoticeId.current, tone: "info",
      message: "No changes detected. No approval request was created." });
  }, []);
  const currentUser = readItemsFamiliesUser();
  const businessUnitId = useRef(currentUser?.active_business_unit_id).current;
  const access = useCatalogAccess(businessUnitId);
  const editPermissions = ["purchasing.items.create", "purchasing.items.update"];
  const canEdit = access.can(editPermissions);
  const canChangeAvailability = canEdit && access.can("purchasing.items.change_state");
  const canMerge = canEdit && access.can(["purchasing.items.merge", "purchasing.stock.current"]);
  const requestOptions = useCallback(() => {
    const options = access.requestConfig(["purchasing.items.create", "purchasing.items.update"]);
    delete options.params;
    return options;
  }, [access]);
  const corporateItem = managedByAdmin(item) || managedByAdmin(availabilityItem);
  const mergedItem = Boolean(item?.merged_into_item_id || availabilityItem?.merged_into_item_id);

  useEffect(() => {
    if (!isOpen || !item?.id || !canEdit) return undefined;
    let cancelled = false;
    const stamp = access.capture();
    const isCurrent = () => access.isCurrent(stamp, ["purchasing.items.create", "purchasing.items.update"]);
    const scopeRef = frequencyScope;
    const scope = ++scopeRef.current;

    const loadData = async () => {
      setLoading(true);
      setLoadFailed(false);
      setForm(buildEmptyForm());
      setDetailsTab("general"); detailsSession.current = { committed: false };
      setSaving(false);
      setAvailabilityBusy(false);
      setAvailabilityItem(null);
      setError("");
      setPendingRequest(null);
      loadedSupplierIds.current = [];
      loadedQuantitySettings.current = { pars: null, frequencies: null };
      setConsumptionFrequencies({}); setConsumptionLocations([]); setFrequenciesAvailable(false); setRetryingFrequencies(false);

      try {
        const [categoriesRes, unitsRes, suppliersRes, itemRes, parStockRes, familiesRes, subfamiliesRes, frequenciesRes] = await Promise.all([
          axios.get(`/api/purchasing/categories?business_unit_id=${businessUnitId}`, requestOptions()),
          axios.get(`/api/purchasing/units?business_unit_id=${businessUnitId}`, requestOptions()),
          axios.get(`/api/purchasing/suppliers/options?business_unit_id=${businessUnitId}`, requestOptions()),
          axios.get(`/api/purchasing/items/${item.id}`, requestOptions()),
          axios.get(`/api/purchasing/inventory/par-levels/item/${item.id}`, requestOptions()).catch((parStockError) => {
            console.warn("[ITEMS][EDIT][PAR-STOCK][LOAD][WARN]", parStockError);
            return null;
          }),
          axios.get(`/api/purchasing/families?business_unit_id=${businessUnitId}`, requestOptions()),
          axios.get(`/api/purchasing/subfamilies?business_unit_id=${businessUnitId}`, requestOptions()),
          axios.get(`/api/purchasing/items/${item.id}/consumption-frequencies`, requestOptions()).catch(() => null),
        ]);

        if (cancelled || !isCurrent()) return;

        const itemData = itemRes.data || {};
        setAvailabilityItem(itemData);
        loadedUnitConfig.current = itemData;
        const linkedSuppliers = [...new Set((itemData.item_suppliers || [])
          .map((link) => Number(link?.supplier_id))
          .filter((supplierId) => Number.isSafeInteger(supplierId) && supplierId > 0))];
        loadedSupplierIds.current = linkedSuppliers;

        setCategories(
          [...(categoriesRes.data || [])].sort((left, right) =>
            String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
              sensitivity: "base",
            })
          )
        );
        setUnits(
          [...(unitsRes.data || [])].sort((left, right) =>
            String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
              sensitivity: "base",
            })
          )
        );
        const supplierChoices = [...(suppliersRes.data || [])];
        // Keep the physical link IDs when a grouped supplier is displayed under
        // its principal. These aliases are tokens only, never new-link options.
        for (const link of itemData.item_suppliers || []) {
          const physicalId = Number(link.supplier_id);
          if (!linkedSuppliers.includes(physicalId) || supplierChoices.some((supplier) => Number(supplier.id) === physicalId)) continue;
          const principal = supplierChoices.find((supplier) => Number(supplier.id) === Number(link.canonical_supplier_id)
            || (supplier.supplier_member_ids || []).map(Number).includes(physicalId)) || link.supplier || link.original_supplier;
          if (principal) supplierChoices.push({ ...principal, id: physicalId, linked_alias: true });
        }
        setSuppliers(
          supplierChoices.sort((left, right) =>
            String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
              sensitivity: "base",
            })
          )
        );
        setFamilies(familiesRes.data || []); setSubfamilies(subfamiliesRes.data || []);
        if (Array.isArray(frequenciesRes?.data?.locations) && Array.isArray(frequenciesRes?.data?.consumption_frequencies)) {
          setConsumptionLocations(frequenciesRes.data.locations);
          setConsumptionFrequencies(consumptionFrequencyDraft(frequenciesRes.data.consumption_frequencies));
          loadedQuantitySettings.current.frequencies = JSON.stringify(consumptionFrequencyDraft(frequenciesRes.data.consumption_frequencies));
          setFrequenciesAvailable(true);
        }
        setShowSubunit(Boolean(itemData.subunit_id));
        setShowSubunit2(Boolean(itemData.subunit_id && itemData.subunit2_id));
        setForm({
          code: itemData.code || "",
          supplier_code: itemData.supplier_code || "",
          name: itemData.name || "",
          description: itemData.description || "",
          product_details: itemData.product_details ?? null,
          category: itemData.category || itemData.subcategory?.category || null,
          subcategory: itemData.subcategory || null,
          category_id: itemData.category_id ? String(itemData.category_id) : "",
          subcategory_id: itemData.subcategory_id ? String(itemData.subcategory_id) : "",
          inventory_policy: itemData.inventory_policy || "",
          unit_justifications: itemData.unit_justifications || {},
          unit_id: itemData.unit_id ? String(itemData.unit_id) : "",
          subunit_id: itemData.subunit_id ? String(itemData.subunit_id) : "",
          pack_size: itemData.subunit_id ? itemData.pack_size ?? "" : "",
          minimum_purchase_quantity: itemData.minimum_purchase_quantity ?? "",
          purchase_multiple: itemData.purchase_multiple ?? "",
          subunit2_id: itemData.subunit_id && itemData.subunit2_id ? String(itemData.subunit2_id) : "",
          subunit2_size: itemData.subunit_id && itemData.subunit2_id ? itemData.subunit2_size ?? "" : "",
          suppliers: linkedSuppliers,
        });

        const parStockData = parStockRes?.data || null;
        setParStockAvailable(Boolean(parStockData));
        const loadedPars = {
          general_par_quantity: toParInputValue(parStockData?.general_selected_quantity ?? parStockData?.general_par_quantity),
          general_unit_id: String((parStockData?.general_selected_quantity != null ? parStockData.general_unit_id : itemData.unit_id) || itemData.unit_id || ""),
          locations: Array.isArray(parStockData?.locations)
            ? parStockData.locations.map((location) => ({
                id: Number(location.id),
                name: location.name || "",
                code: location.code || "",
                current_qty: Number(location.current_qty || 0),
                par_quantity: toParInputValue(location.selected_quantity ?? location.par_quantity),
                unit_id: String((location.selected_quantity != null ? location.unit_id : itemData.unit_id) || itemData.unit_id || ""),
              }))
            : [],
        };
        setParStock(loadedPars);
        loadedQuantitySettings.current.pars = JSON.stringify(loadedPars);
      } catch (requestError) {
        console.error("[ITEMS][EDIT][LOAD][ERROR]", requestError);
        if (!cancelled && isCurrent()) {
          setLoadFailed(true);
          showRequestError(requestError, "We could not load this item for editing.");
        }
      } finally {
        if (!cancelled && isCurrent()) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
      if (scopeRef.current === scope) scopeRef.current++;
    };
  }, [businessUnitId, isOpen, item?.id, setError, showRequestError, access, canEdit, requestOptions]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (!form.category_id) {
      setSubcategories([]);
      return undefined;
    }

    let cancelled = false;
    const stamp = access.capture();
    const isCurrent = () => access.isCurrent(stamp, ["purchasing.items.create", "purchasing.items.update"]);

    const loadSubcategories = async () => {
      try {
        const response = await axios.get(
          `/api/purchasing/subcategories?business_unit_id=${businessUnitId}&category_id=${form.category_id}`, requestOptions()
        );
        if (cancelled || !isCurrent()) return;

        const filtered = Array.isArray(response.data)
          ? response.data.filter(
              (subcategory) => String(subcategory?.category_id || "") === String(form.category_id)
            )
          : [];

        setSubcategories(
          filtered.sort((left, right) =>
            String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
              sensitivity: "base",
            })
          )
        );
      } catch (requestError) {
        console.error("[ITEMS][EDIT][SUBCATEGORIES][ERROR]", requestError);
        if (!cancelled && isCurrent()) {
          setSubcategories([]);
        }
      }
    };

    loadSubcategories();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId, form.category_id, isOpen, access, requestOptions]);

  const selectedSuppliers = useMemo(
    () =>
      (form.suppliers || [])
        .map((supplierId) => suppliers.find((supplier) => Number(supplier.id) === Number(supplierId)) || null)
        .filter(Boolean),
    [form.suppliers, suppliers]
  );

  const availableSuppliers = useMemo(
    () =>
      suppliers.filter(
        (supplier) => !supplier.linked_alias && isActivePurchaseSupplier(supplier) && !(form.suppliers || []).some((supplierId) =>
          [supplier.id, ...(supplier.supplier_member_ids || [])].map(Number).includes(Number(supplierId)))
      ),
    [form.suppliers, suppliers]
  );

  const selectedCategory = useMemo(
    () => {
      const category = (String(form.category?.id) === String(form.category_id) ? form.category : null) || categories.find((entry) => String(entry.id) === String(form.category_id));
      if (!category) return null;
      const subfamily = subfamilies.find((entry) => String(entry.id) === String(category.subfamily_id || category.subfamily?.id)) || category.subfamily;
      const family = families.find((entry) => String(entry.id) === String(subfamily?.family_id || subfamily?.family?.id)) || subfamily?.family;
      return { ...category, subfamily: subfamily ? { ...subfamily, family } : null };
    },
    [categories, subfamilies, families, form.category_id, form.category]
  );

  const selectedSubcategory = useMemo(
    () => (String(form.subcategory?.id) === String(form.subcategory_id) ? form.subcategory : null) || subcategories.find((subcategory) => String(subcategory.id) === String(form.subcategory_id)) || null,
    [form.subcategory_id, form.subcategory, subcategories]
  );

  const frequencyUnits = consumptionUnits(form, units);
  const parUnits = useMemo(() => parStockUnits(form, units), [form, units]);
  const baseParUnit = parUnits.find((unit) => String(unit.id) === String(form.unit_id));
  const generalParUnit = parUnits.find((unit) => String(unit.id) === String(parStock.general_unit_id || form.unit_id)) || baseParUnit;
  const isDirectConsumption = isDirectConsumptionItem({ inventory_policy: form.inventory_policy, category: selectedCategory,
    subcategory: selectedSubcategory ? { ...selectedSubcategory, category: selectedCategory } : null });
  const reloadFrequencies = async () => {
    if (!access.can(editPermissions)) return;
    const stamp = access.capture();
    const scope = frequencyScope.current;
    setRetryingFrequencies(true);
    try {
      const response = await axios.get(`/api/purchasing/items/${item.id}/consumption-frequencies`, requestOptions());
      if (scope !== frequencyScope.current || !access.isCurrent(stamp, editPermissions)) return;
      if (!Array.isArray(response.data?.locations) || !Array.isArray(response.data?.consumption_frequencies)) throw new Error("Invalid consumption frequencies response");
      const frequencyDraft = consumptionFrequencyDraft(response.data.consumption_frequencies);
      setConsumptionLocations(response.data.locations); setConsumptionFrequencies(frequencyDraft);
      loadedQuantitySettings.current.frequencies = JSON.stringify(frequencyDraft);
      setFrequenciesAvailable(true); setError("");
    } catch (_) { if (scope === frequencyScope.current) setFrequenciesAvailable(false); }
    finally { if (scope === frequencyScope.current) setRetryingFrequencies(false); }
  };

  const inventoryPolicyOptions = useMemo(
    () =>
      inventoryPolicySelectOptionsForItem({
        category: selectedCategory,
        subcategory: selectedSubcategory
          ? {
              ...selectedSubcategory,
              category: selectedSubcategory.category || selectedCategory,
            }
          : null,
      }),
    [selectedCategory, selectedSubcategory]
  );

  const minimumGeneralParQuantity = useMemo(
    () => (parStock.locations || []).reduce((sum, location) => sum + parStockBaseQuantity(location.par_quantity, location.unit_id, parUnits, form.unit_id), 0),
    [parStock.locations, parUnits, form.unit_id]
  );
  const unitsChanged = itemUnitConfigurationChanged(loadedUnitConfig.current, form);
  const unitPolicy = useItemUnitCompatibility({ form, original: loadedUnitConfig.current, units,
    active: isOpen && canEdit && !loading && !loadFailed && !corporateItem, client: axios, requestOptions, languageId });
  const replacingAllUnits = unitsChanged && !itemUnitConfigurationsOverlap(loadedUnitConfig.current, form);
  const unitConfigurationProblem = itemUnitConfigurationError(form);
  const frequencyDraftChanged = loadedQuantitySettings.current.frequencies !== null
    && loadedQuantitySettings.current.frequencies !== JSON.stringify(consumptionFrequencies);
  const parDraftChanged = loadedQuantitySettings.current.pars !== JSON.stringify(parStock);

  if (!isOpen || !canEdit) return null;
  if (pendingRequest) return <CatalogCreationPendingNotice request={pendingRequest} onClose={onClose} />;

  const handleClose = () => {
    if (editorBusy || mergeOpen) return;
    setForm(buildEmptyForm());
    setSubcategories([]);
    setParStock(EMPTY_PAR_STOCK);
    setParStockAvailable(false);
    setError("");
    onClose();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (error && error === unitConfigurationProblem && ITEM_UNIT_CONFIGURATION_FIELDS.includes(name)) setError("");
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category_id" ? { subcategory_id: "" } : {}),
      ...(name === "subunit_id" && !value ? { pack_size: "", subunit2_id: "", subunit2_size: "" } : {}),
      ...(name === "subunit2_id" && !value ? { subunit2_size: "" } : {}),
    }));
    if (name === "subunit_id" && !value) { setShowSubunit(false); setShowSubunit2(false); }
    if (name === "subunit2_id" && !value) setShowSubunit2(false);
  };

  const handleSupplierAdd = (event) => {
    const supplierId = Number(event.target.value);
    if (!supplierId) return;

    setForm((prev) => ({
      ...prev,
      suppliers: [...(prev.suppliers || []), supplierId],
    }));
  };

  const handleSupplierRemove = (supplierId) => {
    setForm((prev) => ({
      ...prev,
      suppliers: (prev.suppliers || []).filter((current) => Number(current) !== Number(supplierId)),
    }));
  };

  const handleLocationParChange = (locationId, changes) => {
    setParStock((prev) => ({
      ...prev,
      locations: prev.locations.map((location) =>
        Number(location.id) === Number(locationId) ? { ...location, ...changes } : location
      ),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (detailsBusy || !access.can(editPermissions) || editorBusy || mergeOpen || loading || loadFailed || mergedItem) return;
    const stamp = access.capture();
    setDetailsTab("general");
    if (corporateItem) {
      const scope = frequencyScope.current;
      const isCurrent = () => scope === frequencyScope.current && access.isCurrent(stamp, editPermissions);
      setSaving(true);
      setError("");
      try {
        const response = await axios.patch(`/api/purchasing/items/${item.id}/inventory-policy`, {
          inventory_policy: cleanNullable(form.inventory_policy),
        }, access.requestConfig(editPermissions));
        if (!isCurrent()) return;
        if (response.data?.no_changes === true) { showNoChangesNotice(); return; }
        if (isPendingCatalogCreation(response.data)) { setPendingRequest(response.data.request); return; }
        await onUpdated?.(response.data);
        if (isCurrent()) onClose();
      } catch (failure) {
        if (!isCurrent()) return;
        showRequestError(failure, catalogRequestError(failure) || failure.response?.data?.error || "Could not save the inventory policy. Please try again.");
      } finally { if (isCurrent()) setSaving(false); }
      return;
    }

    if (!String(form.code || "").trim() || !String(form.name || "").trim()) {
      setError("Internal code and name are required.");
      return;
    }

    if (hasInternalCodeConflict(form.code, existingRecords, item.id)) {
      setError(duplicateInternalCodeMessage(form.code));
      return;
    }

    if (!form.category_id) {
      setError("Please select a category.");
      return;
    }

    if (unitConfigurationProblem) {
      setError(unitConfigurationProblem);
      return;
    }

    if (unitPolicy.problem) { setError(unitPolicy.problem); return; }

    if (!(form.suppliers || []).length) {
      setError("Link at least one supplier to this item.");
      return;
    }

    let purchaseConstraints;
    try { purchaseConstraints = serializePurchaseConstraints(form, t); }
    catch (validationError) { setError(validationError.message); return; }

    let frequencyPayload;
    if (isDirectConsumption && (!unitsChanged || frequencyDraftChanged)) {
      if (!frequenciesAvailable) { setError("Could not load consumption locations. Reload before saving frequencies."); return; }
      try { frequencyPayload = serializeConsumptionFrequencies(consumptionFrequencies, consumptionLocations, frequencyUnits, form.unit_id, t); }
      catch (validationError) { setError(validationError.message); return; }
    }
    let parStockPayload = null;
    try {
      if (!isDirectConsumption && parStockAvailable && (!unitsChanged || parDraftChanged)) {
        parStockPayload = serializeParStock(
          { quantity: parStock.general_par_quantity, unit_id: parStock.general_unit_id },
          (parStock.locations || []).map((location) => ({ location_id: Number(location.id), quantity: location.par_quantity, unit_id: location.unit_id })),
          parUnits, form.unit_id, t
        );
      }
    } catch (validationError) {
      const minimum = validationError.minimumGeneralParQuantity;
      const minimumLabel = minimum === undefined ? "" : numberFormatter.format(minimum * (generalParUnit?.factor || 1));
      const unitLabel = generalParUnit?.level !== "unit" ? ` ${getUnitName(generalParUnit, languageId)}` : "";
      setError(minimum === undefined ? validationError.message || "Check the par stock values." : `General par stock must be at least ${minimumLabel}${unitLabel}.`);
      return;
    }

    if (unitsChanged && !replacingAllUnits) {
      purchaseConstraints = Object.fromEntries(Object.entries(purchaseConstraints).filter(([field]) =>
        Number(form[field] || 0) !== Number(loadedUnitConfig.current[field] || 0)));
    }
    setSaving(true);
    setError("");
    const scope = frequencyScope.current;

    try {
      const payload = {
        code: String(form.code || "").trim(),
        supplier_code: cleanNullable(String(form.supplier_code || "").trim()),
        name: String(form.name || "").trim(),
        description: cleanNullable(String(form.description || "").trim()),
        product_details: form.product_details,
        category_id: cleanNullable(form.category_id || ""),
        subcategory_id: cleanNullable(form.subcategory_id || ""),
        inventory_policy: cleanNullable(form.inventory_policy || ""),
        unit_id: cleanNullable(form.unit_id || ""),
        subunit_id: cleanNullable(form.subunit_id || ""),
        pack_size: form.subunit_id ? cleanNullable(form.pack_size) : null,
        subunit2_id: form.subunit_id ? cleanNullable(form.subunit2_id || "") : null,
        ...unitPolicy.fields,
        subunit2_size: form.subunit_id && form.subunit2_id ? cleanNullable(form.subunit2_size) : null,
        expected_unit_configuration: originalItemUnitConfiguration(loadedUnitConfig.current),
        supplier_ids: [...new Set(form.suppliers.map(Number))],
        expected_supplier_ids: loadedSupplierIds.current,
        ...purchaseConstraints,
        ...(frequencyPayload !== undefined ? { consumption_frequencies: frequencyPayload } : {}),
        ...(parStockPayload ? { par_stock: parStockPayload } : {}),
      };

      const response = await axios.put(`/api/purchasing/items/${item.id}`, payload, access.requestConfig(editPermissions));
      if (scope !== frequencyScope.current || !access.isCurrent(stamp, editPermissions)) return;
      if (response.data?.no_changes === true) { showNoChangesNotice(); setSaving(false); return; }
      if (isPendingCatalogCreation(response.data)) { detailsSession.current.committed = true; setPendingRequest(response.data.request); setSaving(false); return; }

      detailsSession.current.committed = true;
      if (typeof onUpdated === "function") {
        await onUpdated(response.data);
      }

      handleClose();
    } catch (requestError) {
      if (scope !== frequencyScope.current || !access.isCurrent(stamp, editPermissions)) return;
      console.error("[ITEMS][EDIT][SAVE][ERROR]", requestError);
      if (String(requestError.response?.data?.code || "").startsWith("PRODUCT_")) setDetailsTab("details");
      showRequestError(requestError, ITEM_SAVE_ERRORS[requestError?.response?.data?.code]
        || itemUnitSaveError(requestError) || catalogRequestError(requestError, "Error updating item.", form.code));
      setSaving(false);
    }
  };

  const renderInventoryPolicy = () => (
    <div className="spx-field spx-field--full">
      <label className="spx-label" htmlFor="item_inventory_policy">
        {t("Inventory Policy")}
      </label>
      <select
        id="item_inventory_policy"
        name="inventory_policy"
        value={form.inventory_policy}
        onChange={handleChange}
        className="spx-select"
        disabled={!canEdit || editorBusy || loadFailed || mergedItem}
      >
        {inventoryPolicyOptions.map((option) => (
          <option key={option.value || "inherit"} value={option.value}>
            {t(option.label)}
          </option>
        ))}
      </select>
      <div className="ifm-fieldHint">
        {t(corporateItem ? "This business unit decides whether the item is stockable or direct consumption. Corporate catalogue updates will preserve this choice." : "Item policy overrides its hierarchy. Choose Inherit to follow subcategory, category, subfamily or family.")}
      </div>
    </div>
  );

  return (
    <div
      className="pilcore-purchasing-overlay pu-item-dialog spx-modalBackdrop"
      data-theme={themeId}
      aria-hidden={mergeOpen || undefined}
      inert={mergeOpen ? "" : undefined}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div ref={modalRef} tabIndex={-1} className="spx-modal spx-modal--wide" role="dialog" aria-modal="true" aria-label={t("Edit item")}>
        <div className="spx-modalHeader">
          <div>
            <div className="spx-modalKicker">{t("Item Workspace")}</div>
            <h3 className="spx-modalTitle">{t("Edit Item")}</h3>
            <p className="spx-modalSubtitle">
              {t(corporateItem ? "Corporate catalogue details are managed centrally. Inventory policy is managed by this business unit." : "Update catalog identity, hierarchy, units and supplier links without leaving the Pilcore catalog workspace.")}
            </p>
          </div>

          <button type="button" className="spx-closeBtn" onClick={handleClose} disabled={editorBusy} aria-label={t("Close edit item popup")}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="spx-modalBody" onInvalidCapture={(event) => revealItemInvalid(event, setDetailsTab)}>
          <ItemDetailsTabs id="edit-item" active={detailsTab} onChange={setDetailsTab} languageId={languageId} />
          {loading ? (
            <div className="spx-empty">{t("Loading item data...")}</div>
          ) : (
            <>
              <div id="edit-item-general-panel" role="tabpanel" aria-labelledby="edit-item-general-tab" hidden={detailsTab !== "general"}>
              {onOpenMerge && !corporateItem && <div className="iep-mergeActions">
                {canMerge && <button type="button" className="spx-btn spx-btn--ghost" disabled={editorBusy || loadFailed}
                  onClick={() => onOpenMerge("merge")}>{t("Merge items")}</button>}
                <button type="button" className="spx-btn spx-btn--ghost" disabled={editorBusy || loadFailed}
                  onClick={() => onOpenMerge("history")}>{t("Merges")}</button>
              </div>}
              {corporateItem && <div className="spx-formGrid">{renderInventoryPolicy()}</div>}
              <fieldset className="spx-formGrid" disabled={!canEdit || editorBusy || loadFailed || corporateItem || mergedItem}>
                <div className="spx-field">
                  <label className="spx-label" htmlFor="item_code">
                    {t("Internal Code")}
                  </label>
                  <input
                    id="item_code"
                    type="text"
                    name="code"
                    value={form.code}
                    onChange={handleChange}
                    className="spx-input"
                    required
                  />
                </div>

                <div className="spx-field">
                  <label className="spx-label" htmlFor="item_supplier_code">
                    {t("Supplier Item Code")}
                  </label>
                  <input
                    id="item_supplier_code"
                    type="text"
                    name="supplier_code"
                    value={form.supplier_code}
                    onChange={handleChange}
                    className="spx-input"
                    placeholder={t("Optional supplier-side reference")}
                  />
                </div>

                <div className="spx-field spx-field--full">
                  <label className="spx-label" htmlFor="item_name">
                    {t("Item Name")}
                  </label>
                  <input
                    id="item_name"
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="spx-input"
                    required
                  />
                </div>

                <div className="spx-field spx-field--full">
                  <ItemClassificationPicker value={{ ...form, category: selectedCategory, subcategory: selectedSubcategory }}
                    disabled={saving || loadFailed || corporateItem}
                    onChange={(classification) => setForm((previous) => ({ ...previous, ...classification }))} />
                </div>

                {!corporateItem && renderInventoryPolicy()}

                {unitsChanged && !unitConfigurationProblem && <div className="spx-field spx-field--full">
                  <p className="ifm-fieldHint" role="status">
                    {t(replacingAllUnits
                      ? "You can replace all units on an unused item. Check the minimum purchase and purchase multiple in the new main unit before saving."
                      : "Saving this unit change will recalculate the item's inventory and historical quantities using the new conversion. Document totals will be preserved. Unedited quantity settings will be converted automatically.")}
                  </p>
                </div>}
                <div className="item-purchase-fields">
                  <div className="spx-field">
                    <ItemUnitPicker label={t("Unit")} value={form.unit_id} units={units} t={t}
                      disabled={saving || loadFailed || corporateItem}
                      onChange={(value) => handleChange({ target: { name: "unit_id", value } })} />
                  </div>
                  {purchaseConstraintFields.map(({ name, label, hint }) => <div className="spx-field" key={name}>
                    <label className="spx-label" htmlFor={`item_${name}`}>{t(label)}</label>
                    <input id={`item_${name}`} name={name} type="number" min="0.0001" max="9999999999.9999" step="0.0001"
                      value={form[name]} onChange={handleChange} className="spx-input" placeholder={t("No restriction")}
                      aria-describedby={`item_${name}_hint`} />
                    <div className="ifm-fieldHint" id={`item_${name}_hint`}>{t(hint)}</div>
                  </div>)}
                </div>

                {form.unit_id && (!showSubunit ? <div className="spx-field spx-field--full">
                  <button type="button" className="spx-btn spx-btn--ghost" onClick={() => setShowSubunit(true)}>{t("Add subunit")}</button>
                </div> : <div className={`item-subunit-row${form.subunit_id ? "" : " item-subunit-row--empty"}`}>
                  <div className="spx-field">
                    <ItemUnitPicker label={t("Subunit")} value={form.subunit_id} units={units} optional t={t}
                      disabled={saving || loadFailed || corporateItem}
                      onChange={(value) => handleChange({ target: { name: "subunit_id", value } })} />
                    {form.subunit2_id && <div className="ifm-fieldHint">{t("Removing this subunit also removes subunit 2.")}</div>}
                  </div>
                  <div className="spx-field item-subunit-packField">
                    {form.subunit_id && <label className="spx-label" htmlFor="item_pack_size">{t("Pack Size")}</label>}
                    <div className="item-subunit-packControl">
                      {form.subunit_id && <input id="item_pack_size" type="number" step="0.01" name="pack_size"
                        value={form.pack_size} onChange={handleChange} className="spx-input" />}
                      <button type="button" className="item-subunit-remove" aria-label={t("Remove subunit")}
                        title={t("Remove subunit")} onClick={() => handleChange({ target: { name: "subunit_id", value: "" } })}><FiTrash2 aria-hidden="true" /></button>
                    </div>
                  </div>
                  <ItemSubunitPreview unitId={form.unit_id} subunitId={form.subunit_id} factor={form.pack_size}
                    itemName={form.name} units={units} languageId={languageId} />
                </div>)}

                {form.unit_id && form.subunit_id && (!showSubunit2 ? <div className="spx-field spx-field--full">
                  <button type="button" className="spx-btn spx-btn--ghost" onClick={() => setShowSubunit2(true)}>{t("Add subunit 2")}</button>
                </div> : <div className={`item-subunit-row${form.subunit2_id ? "" : " item-subunit-row--empty"}`}>
                  <div className="spx-field">
                    <ItemUnitPicker label={t("Subunit 2")} value={form.subunit2_id} units={units} optional t={t}
                      disabled={saving || loadFailed || corporateItem}
                      onChange={(value) => handleChange({ target: { name: "subunit2_id", value } })} />
                  </div>
                  <div className="spx-field item-subunit-packField">
                    {form.subunit2_id && <label className="spx-label" htmlFor="item_subunit2_size">{t("Pack Size 2")}</label>}
                    <div className="item-subunit-packControl">
                      {form.subunit2_id && <input id="item_subunit2_size" type="number" step="0.01" name="subunit2_size"
                        value={form.subunit2_size} onChange={handleChange} className="spx-input" />}
                      <button type="button" className="item-subunit-remove" aria-label={t("Remove subunit 2")}
                        title={t("Remove subunit 2")} onClick={() => handleChange({ target: { name: "subunit2_id", value: "" } })}><FiTrash2 aria-hidden="true" /></button>
                    </div>
                  </div>
                  <ItemSubunitPreview unitId={form.subunit_id} subunitId={form.subunit2_id} factor={form.subunit2_size}
                    itemName={form.name} units={units} languageId={languageId} />
                </div>)}

                <ItemUnitCompatibility policy={unitPolicy} setForm={setForm} />

                {isDirectConsumption && <div className="spx-field spx-field--full"><ItemConsumptionFrequencies collapsible locations={consumptionLocations} value={consumptionFrequencies}
                  onChange={setConsumptionFrequencies} units={frequencyUnits} baseUnitId={form.unit_id} languageId={languageId} t={t}
                  disabled={saving || loadFailed || corporateItem} unavailable={!frequenciesAvailable} onRetry={reloadFrequencies} retrying={retryingFrequencies} /></div>}
                {!isDirectConsumption && parStockAvailable ? (
                  <div className="spx-field spx-field--full">
                    <details className="iep-parSection" aria-label={t("Par stock")}>
                      <summary className="iep-parHead">
                        <h4 className="iep-parTitle">{t("Par Stock")}</h4>
                      </summary>
                      <p className="iep-parHint">
                        {t("Set optional targets by location and, separately, across all active locations.")}
                      </p>

                      <div className="iep-parGeneral">
                        <div className="spx-field">
                          <label className="spx-label" htmlFor="item_general_par_stock">
                            {t("General Par Stock")}
                          </label>
                          <div className="item-par-quantity">
                            <input
                              id="item_general_par_stock"
                              type="number"
                              min="0.00000001"
                              step="0.00000001"
                              value={parStock.general_par_quantity}
                              onChange={(event) =>
                                setParStock((prev) => ({ ...prev, general_par_quantity: event.target.value }))
                              }
                              className="spx-input"
                              placeholder={t("Not set")}
                            />
                            <ItemParUnitSelect value={parStock.general_unit_id} baseUnitId={form.unit_id} units={parUnits}
                              onChange={(unitId) => setParStock((prev) => ({ ...prev, general_unit_id: unitId }))}
                              label={`${t("Par stock unit")} · ${t("General")}`} languageId={languageId} t={t}
                              disabled={saving || loadFailed || corporateItem} />
                          </div>
                        </div>
                        <div className="iep-parMinimum">
                          {t("Minimum from location pars")}
                          <strong>{numberFormatter.format(minimumGeneralParQuantity * (generalParUnit?.factor || 1))} {getUnitName(generalParUnit, languageId)}</strong>
                        </div>
                      </div>

                      <div className="iep-parTableWrap">
                        <table className="iep-parTable iep-parTable--units">
                          <thead>
                            <tr>
                              <th>{t("Location")}</th>
                              <th className="iep-parNumeric">{t("Current Stock")}</th>
                              <th className="iep-parNumeric">{t("Par Stock")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parStock.locations.map((location) => {
                              const locationUnit = parUnits.find((unit) => String(unit.id) === String(location.unit_id || form.unit_id)) || baseParUnit;
                              return <tr key={location.id}>
                                <td>
                                  <span className="iep-parLocationName">{getLocationName(location, languageId)}</span>
                                  {location.code ? <span className="iep-parLocationCode">{location.code}</span> : null}
                                </td>
                                <td className="iep-parNumeric">{numberFormatter.format(location.current_qty * (locationUnit?.factor || 1))} {getUnitName(locationUnit, languageId)}</td>
                                <td className="iep-parNumeric">
                                  <div className="item-par-quantity">
                                    <input
                                      type="number"
                                      min="0.00000001"
                                      step="0.00000001"
                                      value={location.par_quantity}
                                      onChange={(event) => handleLocationParChange(location.id, { par_quantity: event.target.value })}
                                      className="spx-input iep-parInput"
                                      placeholder={t("Not set")}
                                      aria-label={t(`Par stock for ${getLocationName(location, languageId)}`)}
                                    />
                                    <ItemParUnitSelect value={location.unit_id} baseUnitId={form.unit_id} units={parUnits}
                                      onChange={(unitId) => handleLocationParChange(location.id, { unit_id: unitId })}
                                      label={`${t("Par stock unit")} · ${getLocationName(location, languageId)}`} languageId={languageId} t={t}
                                      disabled={saving || loadFailed || corporateItem} />
                                  </div>
                                </td>
                              </tr>;
                            })}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  </div>
                ) : null}

                <div className="spx-field spx-field--full">
                  <label className="spx-label" htmlFor="item_description">
                    {t("Description")}
                  </label>
                  <textarea
                    id="item_description"
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    className="spx-textarea"
                    placeholder={t("Optional description or operational notes for this item.")}
                  />
                </div>

                <div className="spx-field spx-field--full">
                  <label className="spx-label" htmlFor="item_add_supplier">
                    {t("Linked Suppliers")}
                  </label>
                  <select
                    id="item_add_supplier"
                    className="spx-select"
                    value=""
                    onChange={handleSupplierAdd}
                    disabled={!availableSuppliers.length}
                  >
                    <option value="">
                      {t(availableSuppliers.length ? "Add supplier" : "All available suppliers are already linked")}
                    </option>
                    {availableSuppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                  <div className="ifm-fieldHint">
                    {t("Keep at least one supplier linked so the item stays operational inside Purchasing flows.")}
                  </div>
                  <div className="ifm-tokenList">
                    {selectedSuppliers.length ? (
                      selectedSuppliers.map((supplier) => (
                        <span key={supplier.id} className="ifm-token">
                          {supplier.name}
                          <button
                            type="button"
                            className="ifm-tokenRemove"
                            onClick={() => handleSupplierRemove(supplier.id)}
                            title={t("Remove supplier")}
                          >
                            ×
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="spx-note">{t("No suppliers linked yet.")}</span>
                    )}
                  </div>
                </div>
              </fieldset>
              </div>
              <div id="edit-item-details-panel" role="tabpanel" aria-labelledby="edit-item-details-tab" hidden={detailsTab !== "details"}>
                <ProductDetailsFields value={form.product_details} onChange={(product_details) => setForm((previous) => ({ ...previous, product_details }))}
                  languageId={languageId} active={detailsTab === "details"} readOnly={corporateItem || mergedItem} disabled={!canEdit || editorBusy || loadFailed}
                  context={{ scope: "local", item_id: item.id }} requestConfig={access.requestConfig(editPermissions)} onBusy={setDetailsBusy} session={detailsSession} />
              </div>

              {error ? <div className="spx-alert" role="alert">{t(error)}</div> : null}

              <div className="spx-modalFooter">
                {availabilityItem && <div className="iep-availability">
                  <ItemAvailabilityControl itemId={item.id} itemName={availabilityItem.name}
                    is_active={availabilityItem.is_active} businessUnitId={businessUnitId} canEdit={canChangeAvailability}
                    locked={saving || loadFailed || mergeOpen || isItemAvailabilityLocked(item) || isItemAvailabilityLocked(availabilityItem)}
                    onBusyChange={setAvailabilityBusy}
                    onApprovalPending={setPendingRequest}
                    onUpdated={(updated) => {
                      setAvailabilityItem((previous) => ({ ...previous, ...updated }));
                      onAvailabilityUpdated?.(updated);
                    }} />
                </div>}
                <button type="button" className="spx-btn spx-btn--ghost" onClick={handleClose} disabled={editorBusy}>
                  {t("Cancel")}
                </button>
                <button type="submit" className="spx-btn spx-btn--primary" disabled={detailsBusy || !canEdit || editorBusy || loadFailed || mergedItem}>
                  {t(saving ? "Saving..." : "Save Changes")}
                </button>
              </div>
            </>
          )}
        </form>
        {formNotice && <ItemFormNotice key={formNotice.id} tone={formNotice.tone}
          message={t(formNotice.message)} closeLabel={t("Dismiss notice")}
          onDismiss={dismissFormNotice} />}
      </div>
    </div>
  );
}
